import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { RbacActionApiKeyEntity } from './rbac-action-api-key.entity';
import { RbacRoleApiKeyEntity } from './rbac-role-api-key.entity';

@Index('keyId', ['keyId'], { unique: true })
@Index('isActive', ['isActive'], {})
@Entity('_api_key')
export class ApiKeyEntity extends BaseEntity {
	/**
	 * The visible API Key identifier (e.g., wkd_mycompany_abc12345678)
	 * Format: wkd_<alias>_<12 random chars>
	 * This is shown to users and used for lookup
	 * The alias is embedded to identify tenant during auth
	 */
	@Column('varchar', { name: 'keyId', length: 128, unique: true })
	public keyId!: string;

	/**
	 * HMAC-SHA256 hash of the secret
	 * Used for authentication verification
	 */
	@Column('varchar', { name: 'secretHash', length: 64 })
	public secretHash!: string;

	/**
	 * Display name for the API key
	 */
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	/**
	 * Optional description/notes about the key's purpose
	 */
	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	/**
	 * workdUserId of the user who created this key
	 * Used for audit purposes only, NOT for permissions
	 */
	@Column('int', { name: 'createdByWorkdUserId', unsigned: true })
	public createdByWorkdUserId!: number;

	/**
	 * IP restrictions with CIDR notation support
	 * e.g., ["192.168.1.0/24", "10.0.0.1"]
	 * null means no IP restrictions
	 */
	@Column('json', { name: 'allowedIps', nullable: true })
	public allowedIps!: Array<string> | null;

	/**
	 * Origin restrictions for CORS-like validation
	 * e.g., ["https://example.com", "https://*.myapp.com"]
	 * null means no origin restrictions
	 */
	@Column('json', { name: 'allowedOrigins', nullable: true })
	public allowedOrigins!: Array<string> | null;

	/**
	 * Expiration date for the key
	 * null means the key never expires
	 */
	@Column('datetime', { name: 'expiresAt', nullable: true, precision: 0 })
	public expiresAt!: Date | null;

	/**
	 * Last time this key was used for authentication
	 */
	@Column('datetime', { name: 'lastUsedAt', nullable: true, precision: 0 })
	public lastUsedAt!: Date | null;

	/**
	 * Whether the key is currently active
	 * Inactive keys cannot be used for authentication
	 */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;

	/**
	 * Previous secret hash for rotation grace period
	 * During rotation, both current and previous secrets are valid
	 */
	@Column('varchar', {
		name: 'previousSecretHash',
		length: 64,
		nullable: true,
	})
	public previousSecretHash!: string | null;

	/**
	 * When the previous secret expires (end of grace period)
	 * After this time, only the current secret is valid
	 */
	@Column('datetime', {
		name: 'previousSecretExpiresAt',
		nullable: true,
		precision: 0,
	})
	public previousSecretExpiresAt!: Date | null;

	/**
	 * Role assignments for this API key
	 */
	@OneToMany(() => RbacRoleApiKeyEntity, (role) => role.apiKey)
	public rbacRoles!: Array<RbacRoleApiKeyEntity>;

	/**
	 * Direct action assignments for this API key
	 */
	@OneToMany(() => RbacActionApiKeyEntity, (action) => action.apiKey)
	public rbacActions!: Array<RbacActionApiKeyEntity>;
}
